import { model as $$i18n, FALLBACK_LOCALE } from '!/i18n'
import { logger } from '@/logger/logger'
import Gr4vyEmbed, { type EmbedInstance } from '@gr4vy/embed-react'
import {
  PaymentFormButtonGroup,
  PrimarySubmitButton,
  SecondaryOpacityFontButton1Button,
} from '@setplex/arcana'
import { useUnit } from 'effector-react'
import { forwardRef, useCallback, useRef, type ForwardedRef } from 'react'
import { useTheme } from '../hooks/useTheme'
import { $gravyTranslations } from '../model'
import {
  GravyEventsEnum,
  type GravyProps,
  type IGr4vyEmbedComponent,
} from './GravyPaymentForm.h'

const Gr4vyEmbedComponent = forwardRef(
  (
    {
      gr4vyId,
      environment,
      token,
      buyerId,
      amount,
      currency,
      country,
      intent,
      externalIdentifier,
      metadata,
      theme,
      onEvent,
      onComplete,
      locale,
    }: IGr4vyEmbedComponent,
    ref: ForwardedRef<EmbedInstance | null>
  ): JSX.Element => (
    <Gr4vyEmbed
      ref={ref}
      gr4vyId={gr4vyId}
      theme={theme}
      amount={amount}
      currency={currency}
      country={country}
      token={token}
      environment={environment as 'production' | 'sandbox' | undefined}
      externalIdentifier={externalIdentifier}
      intent={intent as 'authorize' | 'capture' | 'approve' | undefined}
      buyerId={buyerId}
      store={'ask'} // true, false - Explicitly store the payment method or ask the
      metadata={metadata}
      onEvent={onEvent}
      onComplete={onComplete}
      locale={locale}
    />
  )
)

Gr4vyEmbedComponent.displayName = 'Gr4vyEmbedComponent'
GravyPaymentForm.displayName = 'GravyPaymentForm'

export function GravyPaymentForm({
  transactionFields,
  isReadyToSubmit,
  onBack,
  onGravyRequestSended,
  onOptionsLoaded,
  onPaymentMethodSelected,
  onArgumentError,
  onFormUpdate,
  onTransactionCreated,
  onTransactionFailed,
  onTransactionCancelled,
  onApiError,
  ...rest
}: GravyProps): JSX.Element | null {
  const theme = useTheme({ ...rest })
  const { backButtonTitle, submitButtonTitle } = useUnit($gravyTranslations)
  const locale = useUnit($$i18n.$locale)

  const embed = useRef<EmbedInstance>(null)
  const onSubmit = useCallback(() => {
    if (!embed.current) return
    if (embed.current.submit) {
      onGravyRequestSended()
      embed.current.submit()
    }
  }, [onGravyRequestSended])

  const onComplete = useCallback(() => {
    // Callback with a transaction object.
    // same as transactionCreated !
  }, [])

  const onEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (name: string, data: any) => {
      if (process.env.NODE_ENV === 'development') {
        logger.log('onEvent: ', name, { data })
      }
      switch (name) {
        case GravyEventsEnum.apiError: {
          onApiError(data)
          break
        }
        case GravyEventsEnum.argumentError: {
          onArgumentError(data)
          break
        }
        case GravyEventsEnum.paymentMethodSelected: {
          onPaymentMethodSelected(data)
          break
        }
        case GravyEventsEnum.optionsLoaded: {
          onOptionsLoaded(data)
          break
        }
        case GravyEventsEnum.formUpdate: {
          onFormUpdate(data)
          break
        }
        case GravyEventsEnum.transactionCreated: {
          onTransactionCreated(data)
          break
        }
        case GravyEventsEnum.transactionCancelled: {
          onTransactionCancelled(data)
          /* Returned when a buyer has explicitly cancelled a transaction,
                      e.g. closing a popup or dismissing Apple Pay. This is currently
                      not supported for 3-D Secure transactions and should not be conflated with
                      transaction being declined or an error occuring.
                      */
          break
        }
        case GravyEventsEnum.transactionFailed: {
          onTransactionFailed(data)
          break
        }
      }
    },
    [
      onApiError,
      onArgumentError,
      onPaymentMethodSelected,
      onOptionsLoaded,
      onFormUpdate,
      onTransactionCreated,
      onTransactionCancelled,
      onTransactionFailed,
    ]
  )

  if (!transactionFields) return null

  return (
    <div>
      <Gr4vyEmbedComponent
        ref={embed}
        theme={theme}
        // TODO need to add automatic handling need language
        locale={locale || FALLBACK_LOCALE}
        intent={transactionFields.intent}
        gr4vyId={transactionFields.gr4vyId}
        environment={transactionFields.environment}
        amount={transactionFields.amount}
        currency={transactionFields.currency}
        country={transactionFields.country}
        token={transactionFields.token}
        buyerId={transactionFields.buyerId}
        externalIdentifier={transactionFields.externalIdentifier}
        metadata={transactionFields.metadata}
        onEvent={onEvent}
        onComplete={onComplete}
      />
      <PaymentFormButtonGroup>
        <PrimarySubmitButton
          disabled={!isReadyToSubmit}
          onClick={onSubmit}
          title={submitButtonTitle}
        />
        <SecondaryOpacityFontButton1Button
          onClick={onBack}
          title={backButtonTitle}
        />
      </PaymentFormButtonGroup>
    </div>
  )
}
